'use client'

/* eslint-disable no-param-reassign */
import { colorsPrimaryNames } from '@/constants/constants'
import { HandsOnLearningSectionProps } from '@/widgets/landing-page-widgets/HandsOnLearningSection/schema'
import { Colors, Typography } from '@pw-tech/omni-ui'
import { useInView } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'
import Featurevideo from './FeatureVideo'
import GetStartedButton from './GetStartedButton'
import MenuCard from './MenuCard'


const VOLUME = 0.12

const HandsOnLearningSection = ({
  handsOnLearningData,
  handleOpenBookDemoModal,
}: {
  handsOnLearningData: HandsOnLearningSectionProps
  handleOpenBookDemoModal: ({ clickFrom }: { clickFrom: string }) => void
}) => {
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
  const [isMuted, setIsMuted] = useState(false)
  const [isPlayingStarted, setIsPlayingStarted] = useState(false)
  const [activeInd, setActiveInd] = useState(0)
  const hOLSectionRef = useRef<HTMLDivElement>(null)
  const isHOLSectionInView = useInView(hOLSectionRef, { amount: 'some' })

  const activeColorPrimaryName = colorsPrimaryNames[
    activeInd % (colorsPrimaryNames?.length || 0)
  ] as keyof typeof Colors

  const activeColor300 = Colors?.[`${activeColorPrimaryName}-300` as keyof typeof Colors]
  const activeColor25 = Colors?.[`${activeColorPrimaryName}-25` as keyof typeof Colors]

  const handleCardClick = (index: number) => {
    setActiveInd(index)
  }

  const handleVideoEnd = useCallback(() => {
    setActiveInd(prev => (prev + 1) % handsOnLearningData.features.length)
  }, [handsOnLearningData.features.length])

  useEffect(() => {
    if (isHOLSectionInView && !isPlayingStarted) {
      setIsPlayingStarted(true)
      
      if (videoRefs.current[0]) {
        const video = videoRefs.current[0]
        
        // Try to autoplay, if it fails try with muted
        const playPromise = video.play()
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            if (error.name === 'NotAllowedError') {
              // Try playing muted as fallback
              video.muted = true
              setIsMuted(true)
              const mutedPromise = video.play()
              if (mutedPromise !== undefined) {
                mutedPromise.catch(() => {
                  // Silently fail - autoplay blocked entirely
                })
              }
            }
          })
        }
        
        video.volume = VOLUME
      }
      setActiveInd(0)
    } else if (videoRefs.current[activeInd]) {
      videoRefs.current[activeInd]?.pause()
    }
  }, [isHOLSectionInView])

  useEffect(() => {
    if (isHOLSectionInView) {
      videoRefs.current.forEach((video, index) => {
        if (video) {
          if (index === activeInd) {
            const playPromise = video.play()
            if (playPromise !== undefined) {
              playPromise.catch((error) => {
                if (error.name === 'NotAllowedError') {
                  // Silently handle autoplay prevention - this is expected
                } else {
                  console.error('Error playing video:', error)
                }
              })
            }
            video.volume = VOLUME
          } else {
            video.pause()
            video.currentTime = 0
          }
        }
      })
    }
  }, [activeInd, isHOLSectionInView])

  const toggleVolumeControl = () => {
    setIsMuted(prev => !prev)
    if (videoRefs.current && videoRefs.current[activeInd]) {
      ;(videoRefs.current as HTMLVideoElement[])[activeInd].muted = !isMuted
    }
  }

  return (
    <div
      style={{
        background: `radial-gradient(52.27% 50% at 50% 50%, ${activeColor300}33 0%, ${activeColor25}33 100%)`,
      }}
    >
  <div className="max-w-6xl mx-auto py-6 md:py-[60px] px-4 md:px-6">
  <div className="flex flex-col gap-4 md:gap-14">
          <div className="text-center">
            <Typography variant='heading2' className="mb-2" weight='bold' color='#1b2124'>
              {handsOnLearningData.heading}
            </Typography>
            <Typography variant='heading4' color='#3d3d3d'>
              {handsOnLearningData.subHeading}
            </Typography>
          </div>

          <div className="flex lg:flex-row flex-col-reverse gap-4" ref={hOLSectionRef}>
            <div className="flex flex-col md:gap-4 gap-2 lg:w-[380px]">
              {handsOnLearningData.features.map((feature, index) => (
                <MenuCard
                  key={feature.title}
                  feature={feature}
                  isActive={activeInd === index}
                  onClick={() => handleCardClick(index)}
                />
              ))}
            </div>

            <div className="flex-1">
              <Featurevideo
                key={activeInd}
                src={handsOnLearningData.features[activeInd]?.videoUrl}
                thumbnail={handsOnLearningData.features[activeInd]?.thumbnail}
                onEnded={handleVideoEnd}
                // eslint-disable-next-line no-return-assign
                ref={(el: any) => (videoRefs.current[activeInd] = el)}
                isMuted={isMuted}
                toggleVolumeControl={toggleVolumeControl}
              />
            </div>
          </div>

          <div className="flex items-center justify-center">
            <GetStartedButton
              title={handsOnLearningData.getStartedButtonText}
              handleOpenBookDemoModal={handleOpenBookDemoModal}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default HandsOnLearningSection
